// audio.ts — a tiny WebAudio synth. Zero asset pipeline (JUICE-RIG's whole argument at 12 hours):
// SFX are generated in-browser from oscillators, nothing to load, tweak the numbers in feel.json.
//
// AudioContext must start from a user gesture, so we lazily create + resume it on first input.

let ctx: AudioContext | null = null;
let muted = false; // sound is ON by default; the game-footer toggle flips this

export function setMuted(on: boolean): void {
  muted = on;
}
export function isMuted(): boolean {
  return muted;
}

/** Create/resume the context. Call from the first real user gesture (keydown / click). */
export function unlockAudio(): void {
  if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  if (ctx.state === "suspended") void ctx.resume();
}

function audio(): AudioContext | null {
  return ctx;
}

/** One short synth blip. Used by the "sound" effect and, chained, by the pitch ramp. */
export function blip(opts: {
  freq: number;
  durationMs?: number;
  type?: OscillatorType;
  volume?: number;
  when?: number; // seconds offset from now, for scheduling chains
}): void {
  const ac = audio();
  if (!ac || muted) return;
  const { freq, durationMs = 90, type = "square", volume = 0.25, when = 0 } = opts;
  const t0 = ac.currentTime + when;
  const dur = durationMs / 1000;

  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);

  // Fast attack, exponential release — reads as a "tick", not a "beep".
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, volume), t0 + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  osc.connect(gain).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Convert a semitone offset from a root frequency to Hz (equal temperament). */
export function semitone(rootFreq: number, steps: number): number {
  return rootFreq * Math.pow(2, steps / 12);
}
